import React from "react";
import axios from "axios";
import styled, { css, keyframes } from "styled-components";
import { webURL } from "../../../services/API";
import {
  Pause,
  Play,
  Hand,
  Send,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  UserRound,
  Bot,
  MessageSquare,
} from "lucide-react";

type ChatStatus = "active" | "stopped" | "manual";

interface ChatSessionItem {
  _id?: string;
  chatId: string;
  chatKey?: string;
  chatName: string;
  platform: string;
  status: ChatStatus;
  userIdentifier?: string;
  userName?: string;
  sessionUserId?: string;
  sessionUserName?: string;
  lastMessageAt?: string;
  lastMessagePreview?: string;
}

interface ChatMessage {
  isAgent: boolean;
  body: string;
  source?: "user" | "agent" | "manual" | "system";
  registration: string;
}

interface ChatSessionDetail extends ChatSessionItem {
  messages: ChatMessage[];
}

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Manual", value: "manual" },
  { label: "Stopped", value: "stopped" },
] as const;

const platformOptions = [
  { label: "All", value: "all" },
  { label: "Web", value: "web" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Slack", value: "slack" },
  { label: "Email", value: "email" },
] as const;

const POLL_INTERVAL_MS = 5000;
const MAX_VISIBLE_SESSIONS = 180;
const RAW_OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const RAW_HEX_PATTERN = /^[a-f0-9]{28,}$/i;

export function looksInternalIdentifier(value: string): boolean {
  const normalized = String(value || "").trim();
  if (!normalized) return true;
  if (RAW_OBJECT_ID_PATTERN.test(normalized) || RAW_HEX_PATTERN.test(normalized)) return true;
  if (/^user-[a-f0-9]{6}$/i.test(normalized)) return true;
  return false;
}

export function sanitizeName(value?: string): string {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (/^(unknown|unknown user|anonymous|user)$/i.test(normalized)) return "";
  if (looksInternalIdentifier(normalized)) return "";
  return normalized;
}

export function sanitizeIdentifier(value?: string): string {
  const normalized = String(value || "").trim().replace(/@c\.us$|@g\.us$/gi, "");
  if (!normalized) return "";

  let cleaned = normalized;
  for (const platform of ["whatsapp", "web", "slack", "email"]) {
    if (cleaned.toLowerCase().startsWith(`${platform}-`)) {
      cleaned = cleaned.slice(platform.length + 1);
      break;
    }
  }

  cleaned = cleaned.replace(/-agent-.+$/i, "").trim();
  cleaned = cleaned.replace(/-thread-[a-z0-9-]+$/i, "").trim();

  if (!cleaned || looksInternalIdentifier(cleaned)) return "";
  if (/^(unknown|anonymous)$/i.test(cleaned)) return "";
  if (/^\d{8,15}$/.test(cleaned)) return `+${cleaned}`;
  return cleaned;
}

export function formatPlatformLabel(platform?: string): string {
  const key = String(platform || "web").toLowerCase();
  if (key === "whatsapp") return "WhatsApp";
  if (key === "slack") return "Slack";
  if (key === "email") return "Email";
  return "Web";
}

function normalizeChatStatus(value: any): ChatStatus {
  if (value === "manual" || value === "stopped" || value === "active") return value;
  return "active";
}

function normalizeSessionItem(raw: any): ChatSessionItem {
  const chatId = String(raw?.chatId || raw?.publicId || raw?.chatKey || "");
  const chatKey = String(raw?.chatKey || raw?.chatId || "");

  return {
    _id: raw?._id,
    chatId,
    chatKey,
    chatName: String(raw?.chatName || raw?.agentIndex || "AI Agent"),
    platform: String(raw?.platform || "web").toLowerCase(),
    status: normalizeChatStatus(raw?.status),
    userIdentifier: raw?.userIdentifier ? String(raw.userIdentifier) : "",
    userName: raw?.userName ? String(raw.userName) : "",
    sessionUserId: raw?.sessionUserId ? String(raw.sessionUserId) : "",
    sessionUserName: raw?.sessionUserName ? String(raw.sessionUserName) : "",
    lastMessageAt: raw?.lastMessageAt ? String(raw.lastMessageAt) : undefined,
    lastMessagePreview: raw?.lastMessagePreview ? String(raw.lastMessagePreview) : "",
  };
}

function normalizeSessionDetail(raw: any): ChatSessionDetail | null {
  if (!raw) return null;

  const normalized = normalizeSessionItem(raw);
  const messages = Array.isArray(raw?.messages)
    ? raw.messages.map((message: any) => ({
        isAgent: Boolean(message?.isAgent),
        body: String(message?.body || ""),
        source: message?.source,
        registration: String(message?.registration || ""),
      }))
    : [];

  return {
    ...normalized,
    messages,
  };
}

function areSessionListsEqual(previous: ChatSessionItem[], next: ChatSessionItem[]): boolean {
  if (previous === next) return true;
  if (previous.length !== next.length) return false;

  for (let index = 0; index < previous.length; index += 1) {
    const left = previous[index];
    const right = next[index];
    if (
      left.chatId !== right.chatId ||
      left.chatKey !== right.chatKey ||
      left.status !== right.status ||
      left.lastMessageAt !== right.lastMessageAt ||
      left.lastMessagePreview !== right.lastMessagePreview ||
      left.userIdentifier !== right.userIdentifier ||
      left.userName !== right.userName ||
      left.sessionUserName !== right.sessionUserName
    ) {
      return false;
    }
  }

  return true;
}

function areSessionDetailsEqual(previous: ChatSessionDetail | null, next: ChatSessionDetail | null): boolean {
  if (previous === next) return true;
  if (!previous || !next) return false;

  const previousLast = previous.messages[previous.messages.length - 1];
  const nextLast = next.messages[next.messages.length - 1];

  return (
    previous.chatId === next.chatId &&
    previous.chatKey === next.chatKey &&
    previous.status === next.status &&
    previous.lastMessageAt === next.lastMessageAt &&
    previous.messages.length === next.messages.length &&
    previousLast?.registration === nextLast?.registration &&
    previousLast?.body === nextLast?.body
  );
}

export function UserChatsMonitor({
  projectId,
  userToken,
  nodeIndex,
}: {
  projectId: string;
  userToken: string;
  nodeIndex: string;
}) {
  const [sessions, setSessions] = React.useState<ChatSessionItem[]>([]);
  const [selectedChatId, setSelectedChatId] = React.useState("");
  const [selectedChatKey, setSelectedChatKey] = React.useState("");
  const [selectedChat, setSelectedChat] = React.useState<ChatSessionDetail | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = React.useState(false);
  const [isLoadingChat, setIsLoadingChat] = React.useState(false);
  const [manualMessage, setManualMessage] = React.useState("");
  const [isSendingManual, setIsSendingManual] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [platformFilter, setPlatformFilter] = React.useState("all");

  const selectedChatIdRef = React.useRef("");
  const selectedChatKeyRef = React.useRef("");
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
    selectedChatKeyRef.current = selectedChatKey;
  }, [selectedChatId, selectedChatKey]);

  const formatTime = React.useCallback((value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString([], {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const statusLabel = React.useCallback(
    (status: ChatStatus) =>
      status === "active" ? "Active" : status === "manual" ? "Manual" : "Stopped",
    []
  );

  const fetchSessions = React.useCallback(async (silent = false) => {
    if (!projectId || !userToken || !nodeIndex) return;
    if (!silent) setIsLoadingSessions(true);

    try {
      const response = await axios.get(`${webURL}/chats`, {
        params: {
          projectId,
          nodeIndex,
          sessionType: "user",
          limit: 300,
          page: 1,
          ...(statusFilter !== "all" ? { status: statusFilter } : {}),
          ...(platformFilter !== "all" ? { platform: platformFilter } : {}),
        },
        headers: { "x-auth-token": userToken },
      });

      const items: ChatSessionItem[] = (response.data?.items || []).map((item: any) => normalizeSessionItem(item));
      setSessions((previous) => (areSessionListsEqual(previous, items) ? previous : items));

      const currentSelectedKey = selectedChatKeyRef.current;

      if (!currentSelectedKey && items.length) {
        const first = items[0];
        setSelectedChatId(first.chatId);
        setSelectedChatKey(first.chatKey || first.chatId);
      }

      if (currentSelectedKey && items.every((session) => (session.chatKey || session.chatId) !== currentSelectedKey)) {
        const fallback = items[0];
        setSelectedChatId(fallback?.chatId || "");
        setSelectedChatKey(fallback?.chatKey || fallback?.chatId || "");
      }
    } catch (error) {
      console.error("Failed to load chat sessions", error);
    } finally {
      if (!silent) setIsLoadingSessions(false);
    }
  }, [projectId, userToken, nodeIndex, statusFilter, platformFilter]);

  const fetchChatDetail = React.useCallback(
    async (chatIdOverride?: string, chatKeyOverride?: string, silent = false) => {
      const chatId = chatIdOverride || selectedChatIdRef.current;
      const chatKey = chatKeyOverride || selectedChatKeyRef.current;

      if (!chatId || !userToken || !projectId) {
        setSelectedChat(null);
        return;
      }

      if (!silent) setIsLoadingChat(true);
      try {
        const response = await axios.get(`${webURL}/chats/${encodeURIComponent(chatId)}`, {
          params: {
            projectId,
            chatKey: chatKey || undefined,
            sessionType: "user",
          },
          headers: { "x-auth-token": userToken },
        });

        const chat = normalizeSessionDetail(response.data);
        setSelectedChat((previous) => (areSessionDetailsEqual(previous, chat) ? previous : chat));
        if (chat?.chatKey && chat.chatKey !== selectedChatKeyRef.current) {
          setSelectedChatKey(chat.chatKey);
        }
      } catch (error) {
        console.error("Failed to load chat detail", error);
        setSelectedChat(null);
      } finally {
        if (!silent) setIsLoadingChat(false);
      }
    },
    [projectId, userToken]
  );

  React.useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  React.useEffect(() => {
    if (!selectedChatId) {
      setSelectedChat(null);
      return;
    }

    fetchChatDetail(selectedChatId, selectedChatKey);
  }, [selectedChatId, selectedChatKey, fetchChatDetail]);

  React.useEffect(() => {
    if (!projectId || !userToken || !nodeIndex) return;

    const interval = setInterval(() => {
      fetchSessions(true);

      if (selectedChatIdRef.current) {
        fetchChatDetail(selectedChatIdRef.current, selectedChatKeyRef.current, true);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [projectId, userToken, nodeIndex, fetchSessions, fetchChatDetail]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedChat?.messages?.length]);

  const filteredSessions = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sessions;

    return sessions.filter((session) => {
      const bucket = [
        session.chatId,
        session.chatKey,
        session.userIdentifier,
        session.userName,
        session.sessionUserId,
        session.sessionUserName,
        session.chatName,
        session.platform,
        session.lastMessagePreview,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return bucket.includes(term);
    });
  }, [sessions, search]);

  const visibleSessions = React.useMemo(() => {
    const term = search.trim();
    if (term || filteredSessions.length <= MAX_VISIBLE_SESSIONS) {
      return filteredSessions;
    }

    const selectedPosition = filteredSessions.findIndex(
      (session) => (session.chatKey || session.chatId) === selectedChatKey
    );

    if (selectedPosition < 0 || selectedPosition < MAX_VISIBLE_SESSIONS) {
      return filteredSessions.slice(0, MAX_VISIBLE_SESSIONS);
    }

    const halfWindow = Math.floor(MAX_VISIBLE_SESSIONS / 2);
    const start = Math.max(0, selectedPosition - halfWindow);
    const end = start + MAX_VISIBLE_SESSIONS;
    return filteredSessions.slice(start, end);
  }, [filteredSessions, search, selectedChatKey]);

  const displayLabelByChatKey = React.useMemo(() => {
    const labels = new Map<string, string>();
    let fallbackCounter = 1;

    for (const session of sessions) {
      const key = String(session.chatKey || session.chatId || `chat-${fallbackCounter}`);
      const name = sanitizeName(session.userName) || sanitizeName(session.sessionUserName);
      const identifier = sanitizeIdentifier(session.userIdentifier) || sanitizeIdentifier(session.sessionUserId);

      if (name) {
        labels.set(key, name);
      } else if (identifier) {
        labels.set(key, identifier);
      } else {
        labels.set(key, `User ${fallbackCounter}`);
        fallbackCounter += 1;
      }
    }

    return labels;
  }, [sessions]);

  const getDisplayUserLabel = React.useCallback(
    (session?: Partial<ChatSessionItem> | null) => {
      if (!session) return "User";

      const key = String(session.chatKey || session.chatId || "");
      const mapped = key ? displayLabelByChatKey.get(key) : undefined;
      if (mapped) return mapped;

      const name = sanitizeName(session.userName) || sanitizeName(session.sessionUserName);
      if (name) return name;

      const identifier = sanitizeIdentifier(session.userIdentifier) || sanitizeIdentifier(session.sessionUserId);
      if (identifier) return identifier;

      return "User";
    },
    [displayLabelByChatKey]
  );

  const selectedIndex = React.useMemo(
    () => filteredSessions.findIndex((session) => (session.chatKey || session.chatId) === selectedChatKey),
    [filteredSessions, selectedChatKey]
  );

  const selectSession = (session: ChatSessionItem) => {
    setSelectedChatId(session.chatId);
    setSelectedChatKey(session.chatKey || session.chatId);
    setManualMessage("");
  };

  const jumpSession = (delta: number) => {
    if (!filteredSessions.length) return;
    if (selectedIndex < 0) {
      selectSession(filteredSessions[0]);
      return;
    }

    const nextIndex = selectedIndex + delta;
    if (nextIndex < 0 || nextIndex >= filteredSessions.length) return;
    selectSession(filteredSessions[nextIndex]);
  };

  const updateStatus = async (action: "stop" | "resume" | "manual") => {
    if (!selectedChatId || !userToken || !projectId) return;

    try {
      await axios.patch(
        `${webURL}/chats/${encodeURIComponent(selectedChatId)}/${action}`,
        {},
        {
          params: {
            projectId,
            chatKey: selectedChatKey || undefined,
            sessionType: "user",
          },
          headers: { "x-auth-token": userToken },
        }
      );
      await Promise.all([
        fetchSessions(true),
        fetchChatDetail(selectedChatIdRef.current, selectedChatKeyRef.current, true),
      ]);
    } catch (error) {
      console.error("Failed to update chat status", error);
    }
  };

  const sendManualMessage = async () => {
    if (!selectedChatId || !manualMessage.trim() || !userToken || !projectId || selectedChat?.status !== "manual") {
      return;
    }

    setIsSendingManual(true);
    try {
      await axios.post(
        `${webURL}/chats/${encodeURIComponent(selectedChatId)}/manual-message`,
        { message: manualMessage.trim() },
        {
          params: {
            projectId,
            chatKey: selectedChatKey || undefined,
            sessionType: "user",
          },
          headers: { "x-auth-token": userToken },
        }
      );
      setManualMessage("");
      await Promise.all([
        fetchSessions(true),
        fetchChatDetail(selectedChatIdRef.current, selectedChatKeyRef.current, true),
      ]);
    } catch (error) {
      console.error("Failed to send manual message", error);
    } finally {
      setIsSendingManual(false);
    }
  };

  const selectedDisplayLabel = getDisplayUserLabel(selectedChat);
  const selectedPlatformLabel = formatPlatformLabel(selectedChat?.platform);

  return (
    <MonitorShell>
      <SidebarPanel>
        <SidebarTop>
          <SidebarTitleWrap>
            <h4>User Chats</h4>
            <span>{isLoadingSessions ? "Refreshing list" : `${sessions.length} sessions`}</span>
          </SidebarTitleWrap>
          <RefreshButton onClick={() => fetchSessions()} title="Refresh chats">
            <RefreshCw size={14} />
          </RefreshButton>
        </SidebarTop>

        <SearchRow>
          <Search size={14} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by user, chat id, preview..."
          />
        </SearchRow>

        <FiltersRow>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)}>
            {platformOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FiltersRow>

        <SessionList>
          {visibleSessions.map((session) => {
            const isActive = (session.chatKey || session.chatId) === selectedChatKey;
            const name = getDisplayUserLabel(session);
            const avatarLabel = String(name).charAt(0).toUpperCase() || "U";

            return (
              <SessionCard key={session.chatKey || session.chatId} $active={isActive} onClick={() => selectSession(session)}>
                <AvatarCircle $status={session.status}>{avatarLabel}</AvatarCircle>
                <SessionBody>
                  <SessionTop>
                    <Name>{name}</Name>
                    <TimeStamp>{formatTime(session.lastMessageAt)}</TimeStamp>
                  </SessionTop>
                  <SessionSubline>
                    <SessionHint>{formatPlatformLabel(session.platform)}</SessionHint>
                    <StatusBadge $status={session.status}>{statusLabel(session.status)}</StatusBadge>
                  </SessionSubline>
                  <Preview>{session.lastMessagePreview || "No messages yet"}</Preview>
                </SessionBody>
              </SessionCard>
            );
          })}

          {!filteredSessions.length && (
            <EmptyList>
              <MessageSquare size={22} />
              <span>No chats match this filter.</span>
            </EmptyList>
          )}
        </SessionList>
      </SidebarPanel>

      <ConversationPanel>
        {!selectedChatId ? (
          <EmptyPane>
            <MessageSquare size={28} />
            <span>Select a chat to view full conversation.</span>
          </EmptyPane>
        ) : isLoadingChat ? (
          <EmptyPane>
            <span>Loading conversation...</span>
          </EmptyPane>
        ) : !selectedChat ? (
          <EmptyPane>
            <span>Unable to load this chat session.</span>
          </EmptyPane>
        ) : (
          <>
            <ConversationHeader>
              <ConversationIdentity>
                <IdentityAvatar>
                  <UserRound size={15} />
                </IdentityAvatar>
                <IdentityMeta>
                  <h4>{selectedDisplayLabel}</h4>
                  <span>{selectedPlatformLabel} • {selectedDisplayLabel}</span>
                </IdentityMeta>
                <StatusBadge $status={selectedChat.status}>{statusLabel(selectedChat.status)}</StatusBadge>
              </ConversationIdentity>

              <HeaderActions>
                <NavGroup>
                  <MiniButton onClick={() => jumpSession(-1)} disabled={selectedIndex <= 0} title="Previous chat">
                    <ChevronLeft size={14} />
                  </MiniButton>
                  <MiniButton
                    onClick={() => jumpSession(1)}
                    disabled={selectedIndex < 0 || selectedIndex >= filteredSessions.length - 1}
                    title="Next chat"
                  >
                    <ChevronRight size={14} />
                  </MiniButton>
                </NavGroup>

                <ControlButton
                  onClick={() => updateStatus("stop")}
                  $variant="danger"
                  disabled={selectedChat.status === "stopped"}
                >
                  <Pause size={13} /> Stop Agent
                </ControlButton>
                <ControlButton
                  onClick={() => updateStatus("resume")}
                  $variant="success"
                  disabled={selectedChat.status === "active"}
                >
                  <Play size={13} /> Resume Agent
                </ControlButton>
                <ControlButton
                  onClick={() => updateStatus("manual")}
                  $variant="warning"
                  disabled={selectedChat.status === "manual"}
                >
                  <Hand size={13} /> Take Manual Control
                </ControlButton>
              </HeaderActions>
            </ConversationHeader>

            <MessagesViewport>
              {(selectedChat.messages || []).map((message, index) => {
                const role = message.source === "system" ? "system" : message.isAgent ? "agent" : "user";

                return (
                  <MessageRow key={`${message.registration || ""}-${index}`} $role={role}>
                    {role === "agent" && (
                      <BubbleAvatar $role={role}>
                        <Bot size={13} />
                      </BubbleAvatar>
                    )}
                    {role === "user" && (
                      <BubbleAvatar $role={role}>
                        <UserRound size={13} />
                      </BubbleAvatar>
                    )}

                    <Bubble $role={role}>
                      <MessageText>{message.body}</MessageText>
                      <BubbleMeta>
                        <span>
                          {role === "agent"
                            ? message.source === "manual"
                              ? "Manual"
                              : "Agent"
                            : role === "system"
                            ? "System"
                            : "User"}
                        </span>
                        <span>{formatTime(message.registration)}</span>
                      </BubbleMeta>
                    </Bubble>
                  </MessageRow>
                );
              })}

              {!selectedChat.messages?.length && (
                <EmptyMessages>
                  <MessageSquare size={22} />
                  <span>No messages in this conversation yet.</span>
                </EmptyMessages>
              )}

              <div ref={messagesEndRef} />
            </MessagesViewport>

            <ComposerPanel $manual={selectedChat.status === "manual"}>
              <ComposerHint>
                {selectedChat.status === "manual"
                  ? "Manual mode enabled: your message will be sent directly from operator control."
                  : "Switch to manual mode to send operator replies."}
              </ComposerHint>

              <ComposerRow>
                <textarea
                  value={manualMessage}
                  onChange={(event) => setManualMessage(event.target.value)}
                  placeholder={
                    selectedChat.status === "manual"
                      ? "Write manual response..."
                      : "Manual composer is disabled while chat is active or stopped"
                  }
                  disabled={selectedChat.status !== "manual" || isSendingManual}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      sendManualMessage();
                    }
                  }}
                />
                <button
                  onClick={sendManualMessage}
                  disabled={selectedChat.status !== "manual" || !manualMessage.trim() || isSendingManual}
                >
                  <Send size={14} /> Send
                </button>
              </ComposerRow>
            </ComposerPanel>
          </>
        )}
      </ConversationPanel>
    </MonitorShell>
  );
}

const sidebarEnter = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
`;

const messageEnter = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MonitorShell = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  background: linear-gradient(160deg, #f8fafc 0%, #eef2ff 30%, #f8fafc 100%);
  font-family: "Manrope", "Segoe UI", Tahoma, sans-serif;

  * {
    box-sizing: border-box;
  }

  @media (max-width: 980px) {
    flex-direction: column;
  }
`;

const SidebarPanel = styled.aside`
  border-right: 1px solid #dbe4f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  flex-direction: column;
  width: 300px;
  max-width: 300px;
  flex: 0 0 300px;
  overflow: hidden;
  min-width: 300px;
  animation: ${sidebarEnter} 0.24s ease;

  @media (max-width: 980px) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex: 0 0 auto;
    border-right: none;
    border-bottom: 1px solid #dbe4f0;
  }
`;

const SidebarTop = styled.div`
  padding: 14px 14px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SidebarTitleWrap = styled.div`
  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: 0.1px;
  }

  span {
    font-size: 11px;
    color: #64748b;
  }
`;

const RefreshButton = styled.button`
  border: 1px solid #d5e3f5;
  border-radius: 10px;
  background: #fff;
  color: #0f172a;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #f0f7ff;
    border-color: #9ec5f0;
  }
`;

const SearchRow = styled.div`
  margin: 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border: 1px solid #dbe7f4;
  border-radius: 12px;
  background: #ffffff;
  color: #7b8794;

  input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 12px;
    color: #0f172a;
  }
`;

const FiltersRow = styled.div`
  margin: 10px 14px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  select {
    border: 1px solid #dbe7f4;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12px;
    color: #334155;
    background: #ffffff;
    outline: none;
  }
`;

const SessionList = styled.div`
  margin-top: 10px;
  padding: 0 10px 10px;
  min-height: 0;
  overflow-y: auto;
  scroll-behavior: smooth;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SessionCard = styled.button<{ $active: boolean }>`
  border: 1px solid ${(props) => (props.$active ? "#94bff0" : "#e2e8f0")};
  background: ${(props) =>
    props.$active
      ? "linear-gradient(125deg, rgba(37, 99, 235, 0.10) 0%, rgba(14, 165, 233, 0.08) 100%)"
      : "#ffffff"};
  border-radius: 14px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9ec5f0;
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.07);
    transform: translateY(-1px);
  }
`;

const AvatarCircle = styled.div<{ $status: ChatStatus }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  background: ${(props) =>
    props.$status === "active"
      ? "linear-gradient(135deg, #0ea5e9, #0284c7)"
      : props.$status === "manual"
      ? "linear-gradient(135deg, #f59e0b, #d97706)"
      : "linear-gradient(135deg, #ef4444, #dc2626)"};
`;

const SessionBody = styled.div`
  min-width: 0;
`;

const SessionTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Name = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TimeStamp = styled.div`
  font-size: 10px;
  color: #64748b;
  white-space: nowrap;
`;

const SessionSubline = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SessionHint = styled.div`
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const StatusBadge = styled.span<{ $status: ChatStatus }>`
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;

  ${(props) => {
    if (props.$status === "active") {
      return css`
        color: #166534;
        background: #dcfce7;
      `;
    }
    if (props.$status === "manual") {
      return css`
        color: #92400e;
        background: #fef3c7;
      `;
    }
    return css`
      color: #991b1b;
      background: #fee2e2;
    `;
  }}
`;

const Preview = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyList = styled.div`
  padding: 22px 12px;
  border: 1px dashed #d8e1ee;
  border-radius: 12px;
  color: #64748b;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const ConversationPanel = styled.section`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(180deg, #f9fbff 0%, #f3f7ff 100%);
`;

const ConversationHeader = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #dbe4f0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const ConversationIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IdentityAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
  background: linear-gradient(140deg, #e2e8f0, #cbd5e1);
`;

const IdentityMeta = styled.div`
  min-width: 0;

  h4 {
    margin: 0;
    font-size: 13px;
    color: #0f172a;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 1px;
    font-size: 11px;
    color: #475569;
  }

  small {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    color: #64748b;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const NavGroup = styled.div`
  display: inline-flex;
  gap: 4px;
`;

const MiniButton = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid #d7e2ef;
  background: #ffffff;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #334155;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ControlButton = styled.button<{ $variant: "danger" | "success" | "warning" }>`
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 7px 11px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.16s ease;

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
    filter: grayscale(0.1);
  }

  ${(props) => {
    if (props.$variant === "danger") {
      return css`
        color: #ffffff;
        background: #ef4444;
        &:hover {
          background: #dc2626;
        }
      `;
    }
    if (props.$variant === "success") {
      return css`
        color: #ffffff;
        background: #16a34a;
        &:hover {
          background: #15803d;
        }
      `;
    }
    return css`
      color: #ffffff;
      background: #f59e0b;
      &:hover {
        background: #d97706;
      }
    `;
  }}
`;

const MessagesViewport = styled.div`
  flex: 1;
  min-height: 170px;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessageRow = styled.div<{ $role: "user" | "agent" | "system" }>`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  animation: ${messageEnter} 0.18s ease;

  ${(props) =>
    props.$role === "agent"
      ? css`
          justify-content: flex-end;
          flex-direction: row-reverse;
        `
      : props.$role === "system"
      ? css`
          justify-content: center;
        `
      : css`
          justify-content: flex-start;
        `}
`;

const BubbleAvatar = styled.div<{ $role: "user" | "agent" | "system" }>`
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;

  ${(props) =>
    props.$role === "agent"
      ? css`
          background: linear-gradient(135deg, #2563eb, #0ea5e9);
        `
      : css`
          background: linear-gradient(135deg, #475569, #334155);
        `}
`;

const Bubble = styled.div<{ $role: "user" | "agent" | "system" }>`
  max-width: min(76%, 760px);
  border-radius: 16px;
  padding: 10px 12px;
  border: 1px solid #dbe4f0;
  background: #ffffff;

  ${(props) =>
    props.$role === "agent"
      ? css`
          background: linear-gradient(140deg, #eff6ff, #e0f2fe);
          border-color: #bfdbfe;
        `
      : props.$role === "system"
      ? css`
          max-width: 86%;
          background: #fff7ed;
          border-color: #fed7aa;
          border-radius: 12px;
        `
      : css`
          background: #ffffff;
          border-color: #e2e8f0;
        `}
`;

const MessageText = styled.pre`
  margin: 0;
  font-size: 12px;
  color: #0f172a;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
`;

const BubbleMeta = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 10px;
  color: #64748b;
`;

const EmptyMessages = styled.div`
  border: 1px dashed #dbe4f0;
  border-radius: 14px;
  padding: 20px;
  color: #64748b;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ComposerPanel = styled.div<{ $manual: boolean }>`
  position: sticky;
  bottom: 0;
  border-top: 1px solid #dbe4f0;
  background: ${(props) => (props.$manual ? "#fff" : "#f8fafc")};
  padding: 10px 12px;
`;

const ComposerHint = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-bottom: 8px;
`;

const ComposerRow = styled.div`
  display: flex;
  gap: 8px;

  textarea {
    flex: 1;
    min-height: 44px;
    max-height: 120px;
    resize: vertical;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 12px;
    font-family: inherit;
    color: #0f172a;

    &:focus {
      outline: none;
      border-color: #38bdf8;
      box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.18);
    }

    &:disabled {
      background: #f1f5f9;
      color: #94a3b8;
      cursor: not-allowed;
    }
  }

  button {
    border: none;
    border-radius: 10px;
    padding: 0 14px;
    background: linear-gradient(135deg, #0ea5e9, #0284c7);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    &:disabled {
      background: #cbd5e1;
      cursor: not-allowed;
    }
  }
`;

const EmptyPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
  text-align: center;
  padding: 22px;
`;